import { useLazyDeleteBeneficiaryQuery } from "@/modules/beneficiary/services";
import type { IError } from "@/shared/common/types";
import { useNotify } from "@/shared/hooks";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { Text } from "@ab.uitools/ui-library/components/Text";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  id?: string | null;
  setId: Dispatch<SetStateAction<string | null>>;
  refetch: () => void;
  beneficiaryName: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onDelete?: (id: string) => void;
}

export const DeleteBeneficiaryModal = ({
  id,
  setId,
  refetch,
  beneficiaryName,
  isOpen,
  setIsOpen,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const { showErrorSnackbar, showSuccessSnackbar } = useNotify();
  const [deleteBeneficiary, { isFetching }] = useLazyDeleteBeneficiaryQuery();

  const handleDelete = () => {
    if (id) {
      deleteBeneficiary({ id })
        .unwrap()
        .then(() => {
          setIsOpen(false);
          refetch();
          if (id) onDelete?.(id);
          showSuccessSnackbar(t("alerts.beneficiaryHasBeenSuccessfullyDeleted"));
        })
        .catch((err: IError) => {
          showErrorSnackbar(err);
          setIsOpen(false);
        })
        .finally(() => {
          setId(null);
        });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      onSubmit={handleDelete}
      buttonProps={{
        confirm: {
          buttonText: t("general.delete"),
          type: "danger",
          isLoading: isFetching,
          disabled: isFetching,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
      titleProps={{
        title: t("beneficiary.deleteModal.title"),
        size: "large",
      }}
    >
      <Text>{t("beneficiary.deleteModal.description", { name: beneficiaryName })}</Text>
    </Modal>
  );
};
